import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Switch, 
  FormControlLabel, 
  Paper,
  IconButton,
  Tooltip,
  Collapse,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  ImageList,
  ImageListItem,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { 
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  AddPhotoAlternate as AddPhotoAlternateIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import axios from 'axios';
import useTranslation from '../hooks/useTranslation';
import { toast } from 'react-toastify';
import Navbar from './Navbar';
import { es, enUS } from 'date-fns/locale';

const ITEMS_PER_PAGE = 6;
const MAX_IMAGES = 5;

const TourForm = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    stockLimit: '',
    isOngoing: false,
    tourDate: null,
    availableDates: [],
    price: '',
    currency: 'USD',
    included: [],
    notIncluded: [],
    meetingPoint: '',
    tips: [],
    isPublic: false
  });
  const [showDatesTable, setShowDatesTable] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [images, setImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) {
      fetchTourData();
    }
  }, [id]);

  const fetchTourData = async () => {
    try {
      const response = await axios.get(`/operators/tours/${id}`);
      const tourData = response.data;
      
      // Convert strings back to arrays
      setFormData({
        ...tourData,
        included: tourData.included ? tourData.included.split('\n').filter(item => item.trim() !== '') : [],
        notIncluded: tourData.notIncluded ? tourData.notIncluded.split('\n').filter(item => item.trim() !== '') : [],
        tips: tourData.tips ? tourData.tips.split('\n').filter(item => item.trim() !== '') : []
      });
    } catch (error) {
      console.error('Error fetching tour:', error);
      toast.error(t('pages.tours.operator.tourForm.notifications.loadError'));
    }
  };

  const validateForm = (checkPublicRequirements = false) => {
    const newErrors = {};

    // Basic validation for private tours
    if (!formData.name.trim()) {
      newErrors.name = t('pages.tours.operator.tourForm.validation.nameRequired');
    }

    // Additional validation only if making public
    if (checkPublicRequirements) {
      if (!formData.description.trim()) {
        newErrors.description = t('pages.tours.operator.tourForm.validation.descriptionRequired');
      }
      if (!formData.stockLimit) {
        newErrors.stockLimit = t('pages.tours.operator.tourForm.validation.stockRequired');
      }
      if (!formData.price) {
        newErrors.price = t('pages.tours.operator.tourForm.validation.priceRequired');
      }
      if (!formData.meetingPoint.trim()) {
        newErrors.meetingPoint = t('pages.tours.operator.tourForm.validation.meetingPointRequired');
      }
      if (!formData.included?.length) {
        newErrors.included = t('pages.tours.operator.tourForm.validation.includedRequired');
      }
      if (!formData.notIncluded?.length) {
        newErrors.notIncluded = t('pages.tours.operator.tourForm.validation.notIncludedRequired');
      }
      if (!formData.isOngoing && !formData.tourDate) {
        newErrors.tourDate = t('pages.tours.operator.tourForm.validation.tourDateRequired');
      }
      if (formData.isOngoing && (!formData.availableDates || formData.availableDates.length === 0)) {
        newErrors.availableDates = t('pages.tours.operator.tourForm.validation.availableDatesRequired');
      }
      if (images.length === 0) {
        newErrors.images = t('pages.tours.operator.tourForm.validation.imagesRequired');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate with public requirements if the tour is being made public
    if (!validateForm(formData.isPublic)) {
      toast.error(t('pages.tours.operator.tourForm.validation.fixErrors'));
      return;
    }

    try {
      const dataToSubmit = {
        ...formData,
        // Convert arrays to strings
        included: formData.included?.join('\n') || '',
        notIncluded: formData.notIncluded?.join('\n') || '',
        tips: formData.tips?.join('\n') || '',
        availableDates: formData.isOngoing ? formData.availableDates : [],
        image_url: JSON.stringify(images)
      };

      if (id) {
        await axios.put(`/operators/tours/${id}`, dataToSubmit);
        toast.success(t('pages.tours.operator.tourForm.notifications.updateSuccess'));
      } else {
        await axios.post('/operators/tours', dataToSubmit);
        toast.success(t('pages.tours.operator.tourForm.notifications.createSuccess'));
      }
      history.push('/operator/dashboard');
    } catch (error) {
      console.error('Error saving tour:', error);
      toast.error(id ? t('pages.tours.operator.tourForm.notifications.updateError') : t('pages.tours.operator.tourForm.notifications.createError'));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addAvailableDate = () => {
    setFormData(prev => ({
      ...prev,
      availableDates: [
        { date: new Date(), stock: prev.stockLimit || 0 },
        ...prev.availableDates
      ]
    }));
    // Reset to first page when adding a new date
    setPage(0);
  };

  const removeAvailableDate = (index) => {
    setFormData(prev => ({
      ...prev,
      availableDates: prev.availableDates.filter((_, i) => i !== index)
    }));
  };

  const updateAvailableDate = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      availableDates: prev.availableDates.map((date, i) => 
        i === index ? { ...date, [field]: value } : date
      )
    }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file sizes (5MB = 5 * 1024 * 1024 bytes)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    const oversizedFiles = files.filter(file => file.size > MAX_FILE_SIZE);
    
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(file => file.name).join(', ');
                toast.error(t('pages.tours.operator.tourForm.notifications.imageSizeError'));
      return;
    }

    setIsUploading(true);
    const newImages = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('image', file);

      try {
        const response = await axios.post('/operators/tours/image', formData, {
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({
              ...prev,
              [file.name]: progress
            }));
          }
        });
        newImages.push(response.data.imageUrl);
      } catch (error) {
        console.error('Error uploading image:', error);
        toast.error(t('pages.tours.operator.tourForm.notifications.imageUploadError'));
      }
    }

    setImages(prev => [...prev, ...newImages]);
    setIsUploading(false);
  };

  const handleRemoveImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), '']
    }));
  };

  const handleRemoveItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleItemChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  return (
    <Box sx={{ bgcolor: '#f5f7f9', minHeight: '100vh' }}>
      <Navbar hideFilters sx={{ bgcolor: '#f5f7f9' }} />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push('/operator/dashboard')}
            sx={{ 
              color: '#316C6E',
              '&:hover': { 
                bgcolor: 'rgba(49, 108, 110, 0.08)' 
              }
            }}
          >
            {t('pages.tours.operator.tourForm.backToDashboard')}
          </Button>
        </Box>
        <Paper sx={{ p: 4, bgcolor: '#fff' }}>
          <Typography variant="h4" gutterBottom sx={{ color: '#316C6E', fontWeight: 'bold' }}>
            {id ? t('pages.tours.operator.tourForm.editTitle') : t('pages.tours.operator.tourForm.createTitle')}
          </Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<AddPhotoAlternateIcon />}
                    disabled={isUploading || images.length >= MAX_IMAGES}
                    sx={{ mr: 2 }}
                  >
                    {t('pages.tours.operator.tourForm.uploadImages')}
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                    />
                  </Button>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {images.length}/{MAX_IMAGES} {t('pages.tours.operator.tourForm.images')}
                      <Typography component="span" color="error" sx={{ ml: 1 }}>*</Typography>
                    </Typography>
                                         <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                       {t('pages.tours.operator.tourForm.maxImageSize')}
                     </Typography>
                  </Box>
                </Box>
                
                {images.length > 0 && (
                  <ImageList sx={{ width: '100%', height: 'auto', mb: 2 }} cols={3} rowHeight={200}>
                    {images.map((image, index) => (
                      <ImageListItem key={index} sx={{ position: 'relative' }}>
                        <img
                          src={image}
                          alt={`Tour image ${index + 1}`}
                          loading="lazy"
                          style={{ height: '200px', objectFit: 'cover' }}
                        />
                        <IconButton
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0, 0, 0, 0.5)',
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                          }}
                          onClick={() => handleRemoveImage(index)}
                        >
                          <DeleteIcon sx={{ color: 'white' }} />
                        </IconButton>
                        {uploadProgress[image] !== undefined && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'rgba(0, 0, 0, 0.5)',
                            }}
                          >
                            <CircularProgress
                              variant="determinate"
                              value={uploadProgress[image]}
                              sx={{ color: 'white' }}
                            />
                          </Box>
                        )}
                      </ImageListItem>
                    ))}
                  </ImageList>
                )}
                {errors.images && (
                  <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                    {errors.images}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label={t('pages.tours.operator.tourForm.tourName')}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  multiline
                  rows={4}
                  label={t('pages.tours.operator.tourForm.description')}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  error={!!errors.description}
                  helperText={errors.description}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  type="number"
                  label={t('pages.tours.operator.tourForm.stockLimit')}
                  name="stockLimit"
                  value={formData.stockLimit}
                  onChange={handleChange}
                  error={!!errors.stockLimit}
                  helperText={errors.stockLimit}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  type="number"
                  label={t('pages.tours.operator.tourForm.price') + ' (DOP)'}
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  error={!!errors.price}
                  helperText={errors.price}
                  InputProps={{
                    startAdornment: 'RD$'
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label={t('pages.tours.operator.tourForm.meetingPoint')}
                  name="meetingPoint"
                  value={formData.meetingPoint}
                  onChange={handleChange}
                  error={!!errors.meetingPoint}
                  helperText={errors.meetingPoint}
                />
              </Grid>
              <Grid item xs={12} container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} locale={t('common.locale') === 'es' ? es : enUS}>
                    <DatePicker
                      label={t('pages.tours.operator.tourForm.tourDate')}
                      value={formData.tourDate}
                      onChange={(newValue) => setFormData(prev => ({ ...prev, tourDate: newValue }))}
                      renderInput={(params) => <TextField {...params} fullWidth required />}
                      disabled={formData.isOngoing}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isOngoing}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            isOngoing: e.target.checked,
                            // Clear tourDate if switching to ongoing
                            tourDate: e.target.checked ? null : prev.tourDate,
                            // Initialize availableDates if switching to ongoing
                            availableDates: e.target.checked ? [] : prev.availableDates
                          }));
                        }}
                        name="isOngoing"
                      />
                    }
                    label={t('pages.tours.operator.tourForm.recurringTour')}
                  />
                </Grid>
              </Grid>
              {formData.isOngoing && (
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ mr: 1 }}>
                          {t('pages.tours.operator.tourForm.availableDates')}
                        </Typography>
                        <IconButton 
                          size="small"
                          onClick={() => setShowDatesTable(!showDatesTable)}
                        >
                          {showDatesTable ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </Box>
                      <Tooltip title={t('pages.tours.operator.tourForm.addDate')}>
                        <Button
                          startIcon={<AddIcon />}
                          onClick={addAvailableDate}
                          variant="outlined"
                        >
                          {t('pages.tours.operator.tourForm.addDate')}
                        </Button>
                      </Tooltip>
                    </Box>
                    <Collapse in={showDatesTable}>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('pages.tours.operator.tourForm.date')}</TableCell>
                              <TableCell align="right">{t('pages.tours.operator.tourForm.stock')}</TableCell>
                              <TableCell align="right">{t('pages.tours.operator.tourForm.actions')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {formData.availableDates
                              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                              .map((dateObj, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <LocalizationProvider dateAdapter={AdapterDateFns} locale={t('common.locale') === 'es' ? es : enUS}>
                                    <DatePicker
                                      value={dateObj.date}
                                      onChange={(newDate) => updateAvailableDate(index + page * rowsPerPage, 'date', newDate)}
                                      renderInput={(params) => <TextField {...params} size="small" />}
                                    />
                                  </LocalizationProvider>
                                </TableCell>
                                <TableCell align="right">
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={dateObj.stock}
                                    onChange={(e) => updateAvailableDate(index + page * rowsPerPage, 'stock', e.target.value)}
                                    sx={{ width: 80 }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton 
                                    onClick={() => removeAvailableDate(index + page * rowsPerPage)} 
                                    color="error"
                                    size="small"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                            {formData.availableDates.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={3} align="center">
                                  <Typography color="text.secondary">
                                    {t('pages.tours.operator.tourForm.noDates')}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                        {formData.availableDates.length > 0 && (
                          <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={formData.availableDates.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                          />
                        )}
                      </TableContainer>
                    </Collapse>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {t('pages.tours.operator.tourForm.included')}
                  <Typography component="span" color="error">*</Typography>
                </Typography>
                <List dense>
                  {(formData.included || []).map((item, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <TextField
                        value={item}
                        onChange={(e) => handleItemChange('included', index, e.target.value)}
                        fullWidth
                        multiline
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <IconButton 
                        onClick={() => handleRemoveItem('included', index)}
                        size="small"
                        sx={{ color: '#d32f2f' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleAddItem('included')}
                    sx={{ mt: 1, color: '#316C6E' }}
                  >
                    Add Item
                  </Button>
                </List>
                {errors.included && (
                  <Typography color="error" variant="caption">
                    {errors.included}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {t('pages.tours.operator.tourForm.notIncluded')}
                  <Typography component="span" color="error">*</Typography>
                </Typography>
                <List dense>
                  {(formData.notIncluded || []).map((item, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CancelIcon color="error" fontSize="small" />
                      </ListItemIcon>
                      <TextField
                        value={item}
                        onChange={(e) => handleItemChange('notIncluded', index, e.target.value)}
                        fullWidth
                        multiline
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <IconButton 
                        onClick={() => handleRemoveItem('notIncluded', index)}
                        size="small"
                        sx={{ color: '#d32f2f' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleAddItem('notIncluded')}
                    sx={{ mt: 1, color: '#316C6E' }}
                  >
                    Add Item
                  </Button>
                </List>
                {errors.notIncluded && (
                  <Typography color="error" variant="caption">
                    {errors.notIncluded}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {t('pages.tours.operator.tourForm.tips')}
                </Typography>
                <List dense>
                  {(formData.tips || []).map((item, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <InfoIcon color="info" fontSize="small" />
                      </ListItemIcon>
                      <TextField
                        value={item}
                        onChange={(e) => handleItemChange('tips', index, e.target.value)}
                        fullWidth
                        multiline
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <IconButton 
                        onClick={() => handleRemoveItem('tips', index)}
                        size="small"
                        sx={{ color: '#d32f2f' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleAddItem('tips')}
                    sx={{ mt: 1, color: '#316C6E' }}
                  >
                    Add Item
                  </Button>
                </List>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isPublic}
                      onChange={(e) => {
                        // Validate with public requirements when trying to make public
                        if (e.target.checked && !validateForm(true)) {
                          toast.error(t('pages.tours.operator.tourForm.validation.cannotMakePublic'));
                          return;
                        }
                        setFormData(prev => ({ ...prev, isPublic: e.target.checked }));
                      }}
                      name="isPublic"
                    />
                  }
                  label={t('pages.tours.operator.tourForm.makePublic')}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
              >
                {id ? t('pages.tours.operator.tourForm.updateButton') : t('pages.tours.operator.tourForm.createButton')}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => history.push('/operator/dashboard')}
              >
                {t('pages.tours.operator.tourForm.cancel')}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TourForm; 